import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// @mui material components
import Card from '@mui/material/Card'
import Badge from '@mui/material/Badge'
import Modal from '@mui/material/Modal'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import DataTable from 'examples/Tables/DataTable'
import MDAlert from 'components/MDAlert'
import { Tooltip, IconButton, CardContent, Typography, Grid } from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import CrudService from 'services/cruds-service'
import { AbilityContext } from 'Can'
import { useAbility } from '@casl/react'
import { useNavigate } from 'react-router-dom'
import MDButton from 'components/MDButton'
import { useMaterialUIController } from 'context'

function CampaignList() {
    let { state } = useLocation()
    const navigate = useNavigate();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const ability = useAbility(AbilityContext)
    const [data, setData] = useState([])
    const [totalRecords, setTotalRecords] = useState(0)
    const [totalEvents, setTotalEvents] = useState(0)
    const [tableData, setTableData] = useState([])
    const [notification, setNotification] = useState({
        value: false,
        text: '',
    })
    const [loader, setLoader] = useState(false)

    useEffect(() => {
        ; (async () => {
            setLoader(true)
            const response = await CrudService.getAllCampaign()
            setData(response.data)
            setTotalRecords(response.size || 0)
            setTotalEvents(response.events || 0)
            setLoader(false)
        })()
        document.title = `Rivio | Campaign`
    }, [])

    useEffect(() => {
        if (!state) return
        setNotification({
            value: state.value,
            text: state.text,
        })
    }, [state])

    useEffect(() => {
        setTableData(getRows(data))
    }, [data])

    useEffect(() => {
        if (notification.value === true) {
            let timer = setTimeout(() => {
                setNotification({
                    value: false,
                    text: '',
                })
            }, 5000)
        }
    }, [notification])

    const clickAddHandler = () => {
        navigate('/campaign/new')
    }

    const clickEditHandler = async (id) => {
        navigate(`/campaign/edit/${id}`)
    }

    const clickDeleteHandler = async (e, id) => {
        try {
            if (!confirm('Are you sure you want to delete this Campaign?')) {
                e.nativeEvent.stopImmediatePropagation()
            } else {
                await CrudService.removeCampaign(id)
                // the delete does not send a response
                // so I need to get again the categories to set it and this way the table gets updated -> it goes to the useEffect with data dependecy
                setLoader(true)
                const response = await CrudService.getAllCampaign()
                setData(response.data)
                setNotification({
                    value: true,
                    text: 'The Campaign has been successfully deleted',
                })
                setLoader(false)
            }
        } catch (err) {
            // it sends error is the API is associated with an API
            console.error(err)
            setLoader(false)
            if (err.hasOwnProperty('errors')) {
                setNotification({
                    value: true,
                    text: err.errors[0].title,
                })
            }
            return null
        }
    }

    const getRows = (info) => {
        let updatedInfo = info.map((row) => {
            let color = row?.status_type_id === 1 ? 'success' : 'error'
            let variant = 'contained'
            let badgeContent = row?.StatusType?.status_name

            return {
                type: 'parameters',
                id: row.campaign_id,
                name: row.name,
                description: row.description,
                eventCount: row.eventCount,
                campaignAssociationCount: row.campaignAssociationCount,
                status: (
                    <Badge
                        color={color}
                        variant={variant}
                        badgeContent={badgeContent}
                        style={{
                            display: 'inline-block',
                            whiteSpace: 'nowrap',
                            marginLeft: '20px',
                        }}
                    ></Badge>
                ),
            }
        })
        return updatedInfo
    }

    const dataTableData = {
        columns: [
            { Header: 'ID', accessor: 'id' },
            { Header: 'name', accessor: 'name' },
            { Header: 'Description', accessor: 'description' },
            { Header: 'Total Event', accessor: 'eventCount' },
            { Header: 'Total Association', accessor: 'campaignAssociationCount' },
            { Header: 'Status', accessor: 'status' },
            {
                Header: 'actions',
                disableSortBy: true,
                accessor: '',
                Cell: (info) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            {ability.can('edit', 'campaign') && (
                                <Tooltip title="Edit Campaign">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();  // Prevent triggering onRowRedirect
                                            clickEditHandler(info.cell.row.original.id);
                                        }}
                                    >
                                        <MDTypography>
                                            <EditIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}

                            {ability.can('delete', 'campaign') && (
                                <Tooltip title="Delete Campaign">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();  // Prevent triggering onRowRedirect
                                            clickDeleteHandler(e, info.cell.row.original.id);
                                        }}
                                    >
                                        <MDTypography>
                                            <DeleteIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    )
                },
            },
        ],

        rows: tableData,
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {notification.value && (
                <MDAlert color="info" my="20px">
                    <MDTypography variant="body2" color="white">
                        {notification.text || ''}
                    </MDTypography>
                </MDAlert>
            )}
            <Grid container spacing={2} sx={{ margin: 0, py: 1, gap: '10px' }}>
                <Grid size={8}>
                    <Card sx={{ p: 2, color: darkMode ? '#fff' : '#000' }} >
                        Total Records: {totalRecords}
                    </Card>
                </Grid>
                <Grid size={8}>
                    <Card sx={{ p: 2, color: darkMode ? '#fff' : '#000' }}>
                        Total Events: {totalEvents}
                    </Card>
                </Grid>
            </Grid>
            <MDBox pt={1}>
                <MDBox >
                    <Card>
                        <MDBox
                            p={3}
                            lineHeight={1}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <MDTypography variant="h5" fontWeight="medium">
                                Campaign
                            </MDTypography>
                            {ability.can('create', 'campaign') && (
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    size="small"
                                    type="submit"
                                    onClick={clickAddHandler}
                                >
                                    + Add Campaign
                                </MDButton>
                            )}
                        </MDBox>

                        <DataTable
                            table={dataTableData}
                            canSearch={true}
                            tableHeight="calc(100vh - 140px)"
                            loader={loader}
                            onRowRedirect={(row) => {
                                localStorage.setItem("selectedCampaign", row);
                                navigate("/campaign/dashboard");
                            }}
                        />
                        {/* <MultiSelectDataTable rowData={getRows(data)} selectedRows={selectedRows} setSelectedRows={setSelectedRows}/> */}
                    </Card>
                </MDBox>
            </MDBox>
            <Footer />
        </DashboardLayout>
    )
}

export default CampaignList